import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IEntityTicketReportData } from 'components/Common/EntityTicketPdf';
import EntityScreeningResultsTable from 'components/Screening/tables/entityTable/EntityScreeningResultsTable';

import EntityResultsDownloadModal from '../Modals/EntityResultsDownloadModal';
import {
  BulkHitSelection,
  BulkHitVisibilityOperation,
} from './bulkHitSelection';
import { getEntityHitDocs, toStaticScreeningResults } from './entityHitList';

interface BiteDBAllMatchesTableProps {
  passedDownResults: Array<{ field?: string; docs?: Record<string, unknown>[] }>;
  hitTotal: number;
  entityName: string;
  isLoading: boolean;
  searchQuery: string;
  actionsPortalTarget?: HTMLElement | null;
  onResultCountChange?: (count: number) => void;
  onToggleHitVisibility: (
    id: string,
    reason?: string,
    recordName?: string,
  ) => void;
  removedHitIds: string[];
  databaseId?: string;
  databaseName?: string;
  entityId?: string;
  groupId?: string;
  canAccessVerificationResults?: boolean;
  suppliersData?: {
    website_quality_info?: unknown;
    places_verification_info?: unknown;
    cbr_registry_info?: unknown;
    domain_age_info?: unknown;
    domain_reputation_info?: unknown;
    email_verify_info?: unknown;
    phone_verify_info?: unknown;
    database_name?: string;
  };
  buildEntityScreeningReport?: () => IEntityTicketReportData | null;
  bulkSelectedHits?: Map<string, BulkHitSelection>;
  setBulkSelectedHits?: React.Dispatch<
    React.SetStateAction<Map<string, BulkHitSelection>>
  >;
  parentDatabaseEntityId?: string;
  parentEntityName?: string;
  onBulkUpdateHits?: (
    operation: BulkHitVisibilityOperation,
    comment: string,
  ) => Promise<void>;
  isBulkHiding?: boolean;
}

const BiteDBAllMatchesTable: React.FC<BiteDBAllMatchesTableProps> = ({
  passedDownResults,
  hitTotal,
  entityName,
  isLoading,
  searchQuery,
  actionsPortalTarget,
  onResultCountChange,
  onToggleHitVisibility,
  removedHitIds,
  databaseId,
  databaseName,
  entityId,
  groupId,
  canAccessVerificationResults = true,
  suppliersData,
  buildEntityScreeningReport,
  bulkSelectedHits,
  setBulkSelectedHits,
  parentDatabaseEntityId,
  parentEntityName,
  onBulkUpdateHits,
  isBulkHiding,
}) => {
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [isResultsDownloadModalOpen, setIsResultsDownloadModalOpen] =
    useState(false);

  const docs = useMemo(
    () => getEntityHitDocs(passedDownResults, entityName),
    [passedDownResults, entityName],
  );
  const results = useMemo(
    () => toStaticScreeningResults(docs, isLoading, hitTotal),
    [docs, hitTotal, isLoading],
  );
  const hasVerificationResults = Boolean(
    suppliersData?.website_quality_info ||
      suppliersData?.places_verification_info ||
      suppliersData?.cbr_registry_info ||
      suppliersData?.domain_age_info ||
      suppliersData?.domain_reputation_info ||
      suppliersData?.email_verify_info ||
      suppliersData?.phone_verify_info,
  );

  return (
    <>
      <EntityScreeningResultsTable
        results={results}
        page={page}
        setPage={setPage}
        handleSubmit={() => undefined}
        searchTerm={entityName}
        isStatic={true}
        hideToolbar={true}
        actionsPortalTarget={actionsPortalTarget}
        externalSearchQuery={searchQuery}
        onResultCountChange={onResultCountChange}
        layout='entityHits'
        onToggleHitVisibility={onToggleHitVisibility}
        removedHitIds={removedHitIds}
        // onDownloadResults intentionally not wired — Download Results is
        // hidden on this table per product request.
        // onDownloadResults={() => setIsResultsDownloadModalOpen(true)}
        bulkSelectedHits={bulkSelectedHits}
        setBulkSelectedHits={setBulkSelectedHits}
        parentDatabaseEntityId={parentDatabaseEntityId}
        parentEntityName={parentEntityName}
        onBulkUpdateHits={onBulkUpdateHits}
        isBulkHiding={isBulkHiding}
      />
      <EntityResultsDownloadModal
        open={isResultsDownloadModalOpen}
        onClose={() => setIsResultsDownloadModalOpen(false)}
        databaseId={databaseId}
        databaseName={databaseName || suppliersData?.database_name}
        entityId={entityId}
        entityName={entityName}
        buildEntityScreeningReport={buildEntityScreeningReport}
        groupId={groupId}
        canAccessVerificationResults={canAccessVerificationResults}
        hasVerificationResults={hasVerificationResults}
        onViewReports={() => {
          if (!databaseId) {
            return;
          }
          setIsResultsDownloadModalOpen(false);
          history.push(
            `/database/${databaseId}?tab=Generated%20Reports&page=1`,
          );
        }}
      />
    </>
  );
};

export default BiteDBAllMatchesTable;
